import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { apiClient, errorDetail } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

/** UI minimum — the backend enforces 10 chars for users (8 for extranet). */
const MIN_PASSWORD_LENGTH = 10;

/**
 * Password-reset confirmation (`/reset-password?token=…`).
 *
 * Reads the single-use token from the query string, takes a new password +
 * confirmation (with a client-side min-length and match check), then posts to
 * `POST /api/v1/auth/password/reset`. On 400 (invalid / expired / weak) the
 * backend's RFC-7807 `detail` is surfaced via {@link errorDetail}; on success
 * the user is sent to the login screen with a confirmation flag.
 *
 * A missing token renders an error card with a path back to /forgot-password,
 * so a truncated or expired link degrades gracefully.
 */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t('resetPassword.tooShort', { count: MIN_PASSWORD_LENGTH }));
      return;
    }
    if (password !== confirm) {
      setError(t('resetPassword.noMatch'));
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post('/auth/password/reset', { token, newPassword: password });
      navigate('/login?reset=success', { replace: true });
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted p-4">
        <div className="w-full max-w-sm rounded-lg border border-destructive/50 bg-card p-6 shadow-sm">
          <p className="text-sm text-destructive">{t('resetPassword.noToken')}</p>
          <Link
            to="/forgot-password"
            className="mt-3 inline-block text-xs text-primary underline-offset-2 hover:underline"
          >
            {t('resetPassword.requestNew')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted p-4">
      <div className="w-full max-w-sm rounded-lg border bg-card p-6 shadow-sm">
        <div className="mb-6 flex items-center justify-center gap-2 text-primary">
          <Sparkles className="h-5 w-5" />
          <h1 className="text-2xl font-semibold">{t('resetPassword.title')}</h1>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="new-password">
              {t('resetPassword.newPasswordLabel')}
            </label>
            <Input
              id="new-password"
              type="password"
              autoFocus
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={MIN_PASSWORD_LENGTH}
              disabled={submitting}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {t('resetPassword.hint', { count: MIN_PASSWORD_LENGTH })}
            </p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="confirm-password">
              {t('resetPassword.confirmLabel')}
            </label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={MIN_PASSWORD_LENGTH}
              disabled={submitting}
            />
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <Button
            type="submit"
            disabled={submitting || !password || !confirm}
            className="w-full"
          >
            {submitting ? t('common.saving') : t('resetPassword.submit')}
          </Button>
        </form>
      </div>
    </div>
  );
}
